/**
 * Porteiro das rotas autenticadas.
 *
 * Enquanto `status` é `unknown`, mostra o splash — não a tela de login. A
 * diferença é visível: sem isso, quem já está logado veria o login piscar a
 * cada abertura do app, que é justamente a impressão de "me deslogou" que
 * queremos evitar numa PWA usada o dia inteiro.
 *
 * Este porteiro é ergonomia. **A autorização real é do servidor** — quem
 * contornar a rota no cliente encontra 401 na primeira requisição.
 */
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { SplashScreen } from '../features/auth/SplashScreen';
import { useAuthStore } from './store';

/**
 * A splash fica na tela pelo menos isto na PRIMEIRA abertura: com a sessão em
 * cache a resposta chega em dezenas de ms, e a marca só piscaria. Nas
 * navegações seguintes dentro do app não há espera nenhuma.
 */
const SPLASH_MINIMA_MS = 900;
let jaAbriu = false;

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const refresh = useAuthStore((s) => s.refresh);
  const { pathname, search } = useLocation();
  const [segurando, setSegurando] = useState(!jaAbriu);

  useEffect(() => {
    if (status === 'unknown') void refresh();
  }, [status, refresh]);

  useEffect(() => {
    if (jaAbriu) return;
    jaAbriu = true;
    const t = window.setTimeout(() => setSegurando(false), SPLASH_MINIMA_MS);
    return () => window.clearTimeout(t);
  }, []);

  if (status === 'unknown' || segurando) return <SplashScreen concluindo={status !== 'unknown'} />;

  if (status === 'anonymous') {
    // Guarda para onde a pessoa queria ir: um link de atividade compartilhado
    // no WhatsApp deve levar à atividade depois do login, não à Home.
    return <Navigate to="/entrar" replace state={{ from: `${pathname}${search}` }} />;
  }

  return <>{children}</>;
}
